/**
 * SMS Campaign Scheduled Page — /marketing/sms-campaigns/:id/scheduled
 *
 * Read-only view of an SMS campaign that is waiting to send. Nothing here edits
 * the campaign: a scheduled campaign fires unattended, so an edit made "just for
 * a second" can ship with a mistake before anyone can undo it. The only action
 * is Cancel Send, which drops the campaign back to draft and returns to the
 * editor.
 *
 * The editor, the contacts/excludes sub-pages and the list page all route a
 * scheduled campaign here instead of into the editor.
 */
import { useEffect, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Lock, MessageCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatWallClockInTz } from '@/lib/facilityTimezone';
import { cn } from '@/lib/utils';
import * as marketingSvc from '@/services/marketing.service';
import { SmsPhoneMockup } from '@/components/sms-editor';
import { calcCost, getSmsSegments } from '@/components/sms-editor/sms-editor-constants';

// The API has already converted `scheduledAt` into the account's zone and
// returns it without an offset, so it must be formatted as-is rather than
// re-read through the browser's zone. See `formatWallClockInTz`.
function formatScheduleDate(iso: string, tz: string): string {
  return formatWallClockInTz(iso, tz, { weekday: true });
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="block text-sm font-semibold text-slate-700 mb-1">{label}</div>
      <div className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 break-words min-h-[2.375rem]">
        {value ?? <span className="text-slate-400">—</span>}
      </div>
    </div>
  );
}

export default function SmsCampaignScheduledPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const userTz = useUserTimezone();

  const { data: campaign, isLoading } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id],
    queryFn: () => marketingSvc.getSmsCampaign(id),
    enabled: !!id,
  });

  // Only a scheduled campaign belongs here. Drafts go back to the editor;
  // anything already sent (or sending) goes to its stats.
  useEffect(() => {
    if (!campaign) return;
    if (campaign.status === 'draft') navigate(`/marketing/sms-campaigns/${id}`, { replace: true });
    else if (campaign.status !== 'scheduled') navigate(`/marketing/sms-campaigns/${id}/stats`, { replace: true });
  }, [campaign, id, navigate]);

  const isScheduled = campaign?.status === 'scheduled';

  const { data: attachedLists = [] } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'lists'],
    queryFn: () => marketingSvc.getSmsCampaignLists(id),
    enabled: isScheduled,
  });
  const { data: directContactCount = 0 } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'direct-contact-count'],
    queryFn: () => marketingSvc.getSmsCampaignDirectContactCount(id),
    enabled: isScheduled,
  });
  const { data: excludeContactCount = 0 } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'exclude-contact-count'],
    queryFn: () => marketingSvc.getSmsCampaignExcludeContactCount(id),
    enabled: isScheduled,
  });
  const { data: recipient, isLoading: recipientLoading } = useQuery({
    queryKey: ['marketing', 'sms-campaigns', id, 'recipient-count'],
    queryFn: () => marketingSvc.getSmsCampaignRecipientCount(id),
    enabled: isScheduled,
    staleTime: 0,
  });
  const totalContacts = recipient?.count ?? 0;

  const cancelMut = useMutation({
    mutationFn: () => marketingSvc.cancelSmsSchedule(id),
    onSuccess: () => {
      toast.success('Send cancelled — the campaign is a draft again and can be edited');
      qc.invalidateQueries({ queryKey: ['marketing', 'sms-campaigns'] });
      navigate(`/marketing/sms-campaigns/${id}`, { replace: true });
    },
    onError: () => toast.error('Failed to cancel the scheduled send'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="text-center py-16 text-slate-500">
        Campaign not found.
        <div className="mt-4">
          <Button variant="ghost" onClick={() => navigate('/marketing/sms-campaigns')}>Back to Campaigns</Button>
        </div>
      </div>
    );
  }

  // Redirecting (see effect above) — render nothing rather than a flash of details.
  if (!isScheduled) return null;

  const messageBody = campaign.messageBody ?? '';
  const mediaUrls = campaign.mediaUrls ?? [];
  const cost = calcCost(totalContacts, messageBody, mediaUrls.length > 0);
  const segments = getSmsSegments(messageBody);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/marketing/sms-campaigns')} className="p-1 rounded hover:bg-slate-100">
            <ArrowLeft className="h-5 w-5 text-slate-500" />
          </button>
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          <span className="font-semibold text-slate-800 truncate">Scheduled Campaign: {campaign.name}</span>
          <Badge variant="info" dot>Scheduled</Badge>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="text-amber-700 border border-amber-400 bg-amber-50 hover:bg-amber-100"
          loading={cancelMut.isPending}
          onClick={() => cancelMut.mutate()}
        >
          <XCircle className="h-4 w-4 mr-1" /> Cancel Send
        </Button>
      </div>

      {/* Locked banner */}
      <div className="mx-4 mt-3 flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <Lock className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <div>
            This campaign is scheduled to send on{' '}
            <strong>{campaign.scheduledAt ? formatScheduleDate(campaign.scheduledAt, userTz) : '—'}</strong>
            {' '}and is locked until then.
          </div>
          <div className="mt-0.5 text-amber-700">
            To change anything, use <strong>Cancel Send</strong>, edit and save the draft, then schedule it again.
          </div>
        </div>
      </div>

      {/* Main layout: details + phone preview */}
      <div className="flex flex-1 min-h-0 overflow-hidden mt-3">
        <div className="flex-[3] overflow-y-auto">
          {/* Sticky header (same as the editor) */}
          <div className="sticky top-0 z-10 bg-white px-5 py-4 border-b border-gray-200">
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">SMS Campaign Details</h2>
                </div>
                <div className="flex flex-col items-end gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-600">Unique Contacts</span>
                    {recipientLoading ? (
                      <Spinner className="h-4 w-4" />
                    ) : (
                      <span className="text-2xl font-bold text-sky-500">{totalContacts.toLocaleString()}</span>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xs text-slate-500">Est. Cost:</span>
                    <span className="text-lg font-bold text-orange-500">${cost.total.toFixed(2)}</span>
                  </div>
                  <span className="text-[11px] text-slate-400">{cost.label}</span>
                </div>
            </div>
          </div>

          <div className="p-5 space-y-5">
            {/* Details */}
            <section>
              <div className="space-y-4">
                <DetailRow label="Name" value={campaign.name} />
                <DetailRow label="Description" value={campaign.description} />
                <DetailRow
                  label="Message"
                  value={messageBody
                    ? <span className="whitespace-pre-wrap">{messageBody}</span>
                    : null}
                />
                <DetailRow
                  label="Length"
                  value={`${messageBody.length} characters · ${segments} segment${segments === 1 ? '' : 's'}${mediaUrls.length ? ` · ${mediaUrls.length} media attachment${mediaUrls.length === 1 ? '' : 's'}` : ''}`}
                />
                <DetailRow
                  label="Scheduled For"
                  value={campaign.scheduledAt ? <span className="font-medium">{formatScheduleDate(campaign.scheduledAt, userTz)}</span> : null}
                />
                {/* Only worth calling out when it changes who receives the send. */}
                {campaign.accountUsers && (
                  <DetailRow label="Account Users" value="Excluded from this campaign" />
                )}
              </div>
            </section>

            {/* Recipients */}
            <section>
              <h2 className="text-sm font-semibold text-slate-700 block mb-1">Campaign Contacts</h2>
              <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200" style={{ background: '#f0f7ff' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-sky-500">{directContactCount}</span>
                    <span className="text-sm font-semibold text-slate-700">direct contact(s)</span>
                  </div>
                  <Link to={`/marketing/sms-campaigns/${id}/contacts`} className="text-sm font-medium text-blue-600 hover:underline">
                    View
                  </Link>
                </div>

                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl font-bold text-indigo-500">{attachedLists.length}</span>
                    <span className="text-sm font-semibold text-slate-700">
                      contact {attachedLists.length === 1 ? 'list' : 'lists'}
                    </span>
                  </div>
                  {attachedLists.length === 0 ? (
                    <p className="text-xs text-slate-400 italic py-1">No lists attached to this campaign</p>
                  ) : (
                    <div className="space-y-1.5">
                      {attachedLists.map((li) => (
                        <div key={li.emailListId} className="flex items-center justify-between px-3 py-2 border border-slate-100 rounded-md bg-slate-50">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-semibold text-slate-700 truncate">{li.listName}</span>
                            <span className={cn(
                              'text-[11px] font-semibold px-2 py-0.5 rounded-full',
                              li.listType === 'dynamic' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700',
                            )}>
                              {li.listType === 'dynamic' ? 'Dynamic' : 'Static'}
                            </span>
                          </div>
                          <span className="text-xs text-slate-500 flex-shrink-0">{(li.uniquePhoneCount ?? li.memberCount).toLocaleString()} phones</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between px-4 py-3" style={{ background: '#fff8f8' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-red-500">{excludeContactCount}</span>
                    <span className="text-sm font-semibold text-slate-700">excluded contact(s)</span>
                  </div>
                  <Link to={`/marketing/sms-campaigns/${id}/excludes`} className="text-sm font-medium text-blue-600 hover:underline">
                    View
                  </Link>
                </div>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                Direct contacts and list members are merged at send time. Excluded contacts and account users (if excluded) are removed before sending.
              </p>
            </section>
          </div>
        </div>

        {/* Phone Preview Panel */}
        <div className="flex-[2] bg-slate-100 flex flex-col items-center justify-start pt-6 overflow-y-auto">
          <div className="text-center mb-3 text-sm font-semibold text-slate-500">Preview</div>
          <SmsPhoneMockup messageBody={messageBody} mediaUrls={mediaUrls} headerText="Message Preview" />
        </div>
      </div>
    </div>
  );
}
